import { createApi } from "@reduxjs/toolkit/query/react";
import { StudentTournament } from "@src/pages/student/tournaments/student-tournaments-list/types";
import { CreateUpdateAdditionalDetailsFormPayload } from "@src/pages/task-manager/additional-details-form/AdditionalDetailsForm.types";
import { CreateTournamentPayload } from "@src/pages/task-manager/tournaments/create-tournament/types";
import { TournamentSummaryResponse } from "@src/pages/task-manager/tournaments/tournament-summary/TournamentSummary.types";
import { Tournament } from "@src/pages/task-manager/tournaments/tournaments-table/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { PaginationType, QuizOrTask } from "@src/types/CommonTypes";
import { TournamentRankingForStudentResponse } from "@src/pages/student/tournaments/student-leader-board/types";
interface Reward {
  startPosition: number;
  endPosition: number;
  rewardAmount: number;
}

export interface StudentTournamentResponse {
  statusCode: number;
  httpStatus: string;
  response: TournamentResponse;
}

export interface FileResponse {
  statusCode: number;
  httpStatus: string;
  response: string;
}

export type fileDownloadPayload = {
  [key: string]: string[];
};

export interface TournamentResponse {
  tournamentId: string;
  duration: number;
  studentReference: string;
  tournamentName: string;
  studentName: string;
  attempts: number;
  attended: number;
  marks: number;
  status: string;
  totalQuestions: number;
  totalMarks: number;
  studentAnswerResponseList: StudentAnswerResponse[];
  percentage: number;
  totalTime: number;
  isShowResult: boolean;
  tournamentEndDate: string;
  totalCorrectAnswered: number;
  totalFailedAttempts: number;
  totalIncorrectAnswered: number;
}

export interface StudentAnswerResponse {
  question: Question;
  solution: string;
  actualAnswer: string;
  marks: number;
  time: number;
  submissionCount: number;
  failedSubmitCount: number;
  totalTestCase: number;
  passedTestCase: number;
  status: string;
  submittedOn: string;
}

export interface Question {
  id: string;
  parentId: string;
  subjectReference: string;
  parentType: string;
  parentName: string;
  questionTitle: string;
  type: string;
  sharedFrom: string;
  evaluationType: string;
  difficultyLevel: string;
  sourceCategory: string;
  source: string;
  resource: Resource[];
  scope: string;
  status: string;
  approvedBy: string;
  isSharable: boolean;
  isUsed: boolean;
  additionalDetails: AdditionalDetails;
  version: Version;
  applications: string[];
  isShareQlabs: boolean;
  isShareToOthers: boolean;
  isMultipleChoice: boolean;
  questionProgramType: string;
  questionCategory: string;
  organizationReference: string;
  isShuffleOption: boolean;
  questionNo: number;
}

export interface Resource {
  resourceType: string;
  link: string;
  value: string;
}

export interface AdditionalDetails {
  company: string;
  keywords: string[];
  relatedExams: RelatedExam[];
  relatedQuestionIds: string[];
}

export interface RelatedExam {
  examName: string;
  date: string;
}

export interface Version {
  createdOn: string;
  lastUpdateOn: string;
  createdByName: string;
  createdByEmail: string;
  modifiedByName: string;
  modifiedByEmail: string;
  id: string;
  questionId: string;
  questionDescription: string;
  solution: Solution;
  marks: number;
  duration: number;
  labels: string[];
  testCases: TestCase[];
  isTestCasePresent: boolean;
}

export interface Solution {
  options: string[];
  correctOptions: string[];
  answerExplanation: string;
  code: Code;
  output: string;
}

export interface Code {
  language: string;
  solution: string;
  userTemplate: string;
}

export interface TestCase {
  input: string[];
  expectedOutput: any;
  testCaseType: string;
  testCaseScope: string;
}

export interface getAllTournamentsResponse {
  statusCode: number;
  response: {
    content: Tournament[];
  };
}
export interface getAllStudentTournamentsResponse {
  statusCode: number;
  response: {
    content: StudentTournament[];
  } & PaginationType;
}

export interface CreateUpdateAdditionalDetailsFormResponse extends APIResponse {
  response: QuizOrTask;
}

export type TournamentTableStructure = {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | any[]
    | Record<string, any>;
};

export interface GetTournamentRankingApiResponse extends APIResponse {
  response: TournamentRankingForStudentResponse;
}

export const TournamentsApiSlice = createApi({
  reducerPath: "TournamentsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getAllTournaments", "getAllTournamentsForOrg"],
  endpoints: (builder) => ({
    getAllTournaments: builder.query<
      getAllTournamentsResponse,
      { pageNo: number; pageSize: number; year: number; month: number }
    >({
      query: ({ pageNo, pageSize, year, month }) => ({
        url: `tournament/get-tournaments?pageNo=${pageNo}&pageSize=${pageSize}&year=${year}&month=${month}`,
        method: "GET",
      }),
      providesTags: ["getAllTournaments"],
    }),
    getAllTournamentsForOrganization: builder.query<
      getAllTournamentsResponse,
      {
        pageNo: number;
        pageSize: number;
        year: number;
        month: number;
        organizationId: string;
      }
    >({
      query: ({ pageNo, pageSize, year, month, organizationId }) => ({
        url: `tournament/get-organization-tournaments?pageNo=${pageNo}&pageSize=${pageSize}&year=${year}&month=${month}&organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["getAllTournamentsForOrg"],
    }),
    getTournamentsForStudent: builder.query<
      getAllStudentTournamentsResponse,
      { pageNo: number; pageSize: number; status: string; scope: string }
    >({
      query: ({ pageNo, pageSize, status, scope }) => ({
        url: `tournament/get-tournaments-student?pageNo=${pageNo}&pageSize=${pageSize}&status=${status}&scope=${scope}`,
        method: "GET",
      }),
    }),
    getTournamentRankingForStudent: builder.query<
      GetTournamentRankingApiResponse,
      { tournamentId: string; pageNo: number; pageSize: number; limit?: number }
    >({
      query: ({ pageNo, pageSize, tournamentId, limit }) => {
        const params = new URLSearchParams({
          tournamentId,
          pageNo: pageNo.toString(),
          pageSize: pageSize.toString(),
        });

        if (limit !== undefined) {
          params.append("limit", limit.toString());
        }

        return {
          url: `tournament/get-ranking?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    addNewTournament: builder.mutation<
      any,
      { payload: CreateTournamentPayload; organizationId?: string }
    >({
      query: ({ payload, organizationId }) => {
        const url = organizationId
          ? `tournament?organizationId=${organizationId}`
          : "tournament";

        return {
          url,
          method: "POST",
          body: payload,
        };
      },
    }),

    addRewardsForTournament: builder.mutation<
      any,
      { tournamentId: string; rewards: Reward[] }
    >({
      query: ({ tournamentId, rewards }) => ({
        url: `tournament/add-rewards?tournamentId=${tournamentId}`,
        method: "PATCH",
        body: rewards,
      }),
    }),
    disableTournament: builder.mutation<
      any,
      { tournamentId: string; status: string }
    >({
      query: ({ tournamentId, status }) => ({
        url: `tournament/disable?tournamentId=${tournamentId}&tournamentStatus=${status}`,
        method: "PATCH",
      }),
    }),
    getStudentTournamentResponse: builder.query<
      StudentTournamentResponse,
      { tournamentId: string }
    >({
      query: ({ tournamentId }) => ({
        url: `student/get-student-tournament-response`,
        method: "GET",
        params: {
          tournamentId,
        },
      }),
    }),
    enrollTournament: builder.mutation<any, { tournamentId: string }>({
      query: ({ tournamentId }) => ({
        url: `tournament/enroll-tournament?tournamentId=${tournamentId}`,
        method: "PUT",
      }),
    }),
    getTournamentStatusForStudent: builder.query<any, { tournamentId: string }>(
      {
        query: ({ tournamentId }) => ({
          url: `tournament/get-tournament-status-student?tournamentId=${tournamentId}`,
          method: "GET",
        }),
      },
    ),
    getDuplicatedTournament: builder.query<
      any,
      { tournamentName: string; organizationId: string }
    >({
      query: ({ tournamentName, organizationId }) => ({
        url: `tournament/check-duplicate?tournamentName=${tournamentName}&organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getTournamentById: builder.query<
      TournamentSummaryResponse,
      { tournamentId: string }
    >({
      query: ({ tournamentId }) => ({
        url: `tournament`,
        method: "GET",
        params: {
          tournamentId,
        },
      }),
    }),
    downloadTournamentFile: builder.mutation<
      FileResponse,
      {
        payload: fileDownloadPayload;
        tournamentId?: string;
        isPayloadRequired?: boolean;
      }
    >({
      query: ({ payload, tournamentId, isPayloadRequired }) => {
        const url = `tournament/download-tournament-results?tournamentId=${tournamentId}`;
        const request: any = {
          url,
          method: "POST",
        };
        if (isPayloadRequired) {
          request.body = payload;
        }
        return request;
      },
    }),
    createUpdateAdditionalDetailsFormTournament: builder.mutation<
      CreateUpdateAdditionalDetailsFormResponse,
      {
        tournamentId: string;
        payload: CreateUpdateAdditionalDetailsFormPayload;
        additionalDetailsDuration: number | string;
      }
    >({
      query: ({ tournamentId, payload, additionalDetailsDuration }) => ({
        url: `tournament/add-additional-details`,
        method: "PATCH",
        body: payload,
        params: { tournamentId, additionalDetailsDuration },
      }),
    }),
    tournamentTableStructure: builder.mutation<
      TournamentTableStructure,
      { tournamentId?: string }
    >({
      query: ({ tournamentId }) => {
        const url = `tournament/get-tournament?tournamentId=${tournamentId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useGetAllTournamentsQuery,
  useGetAllTournamentsForOrganizationQuery,
  useLazyGetAllTournamentsForOrganizationQuery,
  useGetTournamentsForStudentQuery,
  useLazyGetTournamentsForStudentQuery,
  useAddRewardsForTournamentMutation,
  useDisableTournamentMutation,
  useGetTournamentRankingForStudentQuery,
  useLazyGetTournamentRankingForStudentQuery,
  useAddNewTournamentMutation,
  useLazyGetStudentTournamentResponseQuery,
  useEnrollTournamentMutation,
  useLazyGetTournamentStatusForStudentQuery,
  useLazyGetDuplicatedTournamentQuery,
  useGetTournamentByIdQuery,
  useDownloadTournamentFileMutation,
  useLazyGetTournamentByIdQuery,
  useCreateUpdateAdditionalDetailsFormTournamentMutation,
  useTournamentTableStructureMutation,
} = TournamentsApiSlice;
